/**
 * TPRA Home Dashboard Constants
 */

import {
  FileText,
  Search,
  Loader2,
  ClipboardCheck,
  CheckCircle2,
  Eye,
  type LucideIcon,
} from "lucide-react";
import type { AssessmentStageType, RiskLevel, BusinessCriticality } from "../types";

// Assessment Stage Configuration
export interface StageConfig {
  label: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

export const ASSESSMENT_STAGE_CONFIG: Record<AssessmentStageType, StageConfig> = {
  intake_form: {
    label: "Intake Form",
    icon: FileText,
    color: "#6366F1",
    bgColor: "#EEF2FF",
  },
  information_gathering: {
    label: "Information Gathering",
    icon: Search,
    color: "#F59E0B",
    bgColor: "#FFFBEB",
  },
  generating_results: {
    label: "Generating Results",
    icon: Loader2,
    color: "#8B5CF6",
    bgColor: "#F5F3FF",
  },
  review_results: {
    label: "Review Results",
    icon: ClipboardCheck,
    color: "#EC4899",
    bgColor: "#FDF2F8",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    color: "#10B981",
    bgColor: "#ECFDF5",
  },
  continuous_monitoring: {
    label: "Continuous Monitoring",
    icon: Eye,
    color: "#0EA5E9",
    bgColor: "#F0F9FF",
  },
};

// Severity Colors for Findings Chart (Figma Design Spec)
export const SEVERITY_COLORS = {
  critical: "#610726", // Dark maroon
  high: "#F31260", // Red
  medium: "#F5A524", // Orange
  low: "#17C964", // Green
} as const;

// Severity Labels for Findings Chart
export const SEVERITY_LABELS = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
} as const;

// Risk Level Colors for Heatmap
export const RISK_LEVEL_COLORS: Record<RiskLevel, string> = {
  High: "#DC2626",
  Medium: "#F97316",
  Low: "#22C55E",
};

// Business Criticality Order (for heatmap X-axis)
export const CRITICALITY_ORDER: BusinessCriticality[] = [
  "Low",
  "Medium",
  "High",
  "Critical",
];

// Risk Level Order (for heatmap Y-axis)
export const RISK_LEVEL_ORDER: RiskLevel[] = ["High", "Medium", "Low"];

// Heatmap Cell Colors (intensity based on count)
export const getHeatmapCellColor = (
  riskLevel: RiskLevel,
  criticality: BusinessCriticality,
  count: number
): string => {
  // Higher risk + higher criticality = more intense color
  const riskWeight = { High: 3, Medium: 2, Low: 1 }[riskLevel];
  const criticalityWeight = { Critical: 4, High: 3, Medium: 2, Low: 1 }[criticality];
  const intensity = riskWeight * criticalityWeight;

  if (count === 0) return "#F4F4F5";
  if (intensity >= 9) return "#FEE2E2"; // High risk + High/Critical criticality
  if (intensity >= 6) return "#FFEDD5"; // Medium combinations
  if (intensity >= 3) return "#FEF3C7"; // Lower combinations
  return "#ECFDF5"; // Low risk + Low criticality
};

// Task Type Labels
export const TASK_TYPE_LABELS = {
  intake_form_pending: "Intake Form Pending",
  due_diligence_pending: "Due Diligence Pending",
} as const;

// Task Type Colors (Figma Design Spec)
export const TASK_TYPE_COLORS = {
  intake_form_pending: "#09AACD", // Cyan
  due_diligence_pending: "#992F7B", // Purple
} as const;
